import type { IDescriptor } from "../descriptor";
import type {
  IGraphMLPredictorConfig,
  ScalarPredictorConfig,
} from "../predictor";
import { isOfType } from "../utils";

/**
 * Documentation from https://github.com/CitrineInformatics/model-assistant-lambda
 */

type ModelExpertLanguageModel =
  | "gpt-3.5-turbo"
  | "gpt-3.5-turbo-16k"
  | "gpt-4"
  | "gpt-4-32k";

export interface ModelExpertQuestionRequest {
  question: string;
  config: IGraphMLPredictorConfig;
  language_model?: ModelExpertLanguageModel;
  /**
   * The model temperature. Must be a floating-point number between 0 and 2.
   * Determines how "creative" the model can be: a lower number means less creative
   * (i.e. more deterministic), and a higher number means more creative. Defaults to 0.
   */
  temperature?: number;
}

interface Target {
  /** field name, i.e. "inputs" or "outputs" if the target is an AutoML node */
  field: string;
  /** individual predictor config name */
  name: string;
}

/** Represents the addition of a descriptor to an existing node */
interface AddDescriptor {
  type: "add-descriptor";
  data: {
    descriptor: IDescriptor;
    /** The name of the subpredictor that the descriptor is coming from */
    source: string;
    /** The name of the node and the field which the descriptor is being added to */
    target: Target;
  };
}

interface AddNode {
  type: "add-node";
  data: {
    /** keys are input descriptor keys with their values being the nodes they are coming from */
    descriptor_sources: Partial<Record<string, ScalarPredictorConfig>>;
    node: ScalarPredictorConfig;
  };
}

interface RemoveDescriptor {
  type: "remove-descriptor";
  data: {
    descriptor: IDescriptor;
    /** This field will always be null simply because of how the BE is typed, can be ignored */
    source?: null;
    /** The name of the node and the field which the descriptor is being removed from */
    target: Target;
  };
}

export type ModifiedConfigChange = AddDescriptor | RemoveDescriptor | AddNode;

export interface ModelExpertModifiedConfigResponse {
  type: "modified-config";
  data: {
    changes: ModifiedConfigChange[];
    config: IGraphMLPredictorConfig;
  };
}

export interface ModelExpertMessageResponse {
  type: "message";
  data: { message: string };
}

export interface ModelExpertUnsupportedResponse {
  type: "unsupported";
  data: { message: string };
}

export type ModelExpertResponse =
  | ModelExpertMessageResponse
  | ModelExpertModifiedConfigResponse
  | ModelExpertUnsupportedResponse;

export const isMessageResponse = isOfType<
  ModelExpertMessageResponse,
  ModelExpertResponse
>("message");

export const isUnsupportedResponse = isOfType<
  ModelExpertUnsupportedResponse,
  ModelExpertResponse
>("unsupported");

export const isModifiedConfigResponse = isOfType<
  ModelExpertModifiedConfigResponse,
  ModelExpertResponse
>("modified-config");
